import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { History } from "lucide-react";
import { Kpi, money } from "@/mockups/dc";
import { ExecFunnel, ExecTrend } from "./ExecCharts";
import { ExecServices, ExecOutcomes, ExecReps, ExecDrill } from "./ExecTables";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PERIODS = [["week", "Week"], ["mtd", "MTD"], ["last30", "Last 30 days"], ["qtd", "QTD"], ["ytd", "YTD"]];
export const OLD_VIEW_REMOVAL = "Nov 7, 2026";

const delta = (a, b, unit, lowerBetter) => {
  if (a === null || b === null || a === undefined || b === undefined) return {};
  const d = Math.round((a - b) * 10) / 10;
  return { trend: `${d > 0 ? "+" : ""}${d}${unit} vs prev`, status: (lowerBetter ? d <= 0 : d >= 0) ? "pos" : "warn" };
};
const mins = (m) => (m === null || m === undefined ? "—" : m >= 120 ? `${Math.round(m / 60)}h` : `${m}m`);
const Tap = ({ onClick, children }) => (
  <div role="button" tabIndex={0} onClick={onClick} onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onClick())} style={{ cursor: "pointer", display: "grid" }}>{children}</div>
);

const Kpis = ({ k, onPick }) => {
  const conv = delta(k.conversion, k.prev_conversion, " pts"), deal = delta(k.avg_revenue_per_won, k.prev_avg_revenue_per_won, "");
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gridAutoRows: "1fr", gap: 12, "--text": "#4A6470" }}>
      <Kpi label="Conversion" value={`${k.conversion}%`} {...conv} testid="lx-kpi-conversion" />
      <Kpi label="Avg revenue per won lead" value={money(k.avg_revenue_per_won)} status={deal.status} trend={deal.trend && `${deal.trend.startsWith("-") ? "−" : "+"}${money(Math.abs(k.avg_revenue_per_won - k.prev_avg_revenue_per_won))} vs prev`} sub="Gingr revenue" testid="lx-kpi-deal" />
      <Tap onClick={() => onPick({ stage: "won", title: "Won", n: k.won })}><Kpi label="Won revenue" value={money(k.won_revenue)} sub={`from ${k.won} won leads · tap to see them`} testid="lx-kpi-revenue" /></Tap>
      <Tap onClick={() => onPick({ open_now: true, title: "Open right now", n: k.open_now })}><Kpi label="Open leads now" value={k.open_now} sub={`avg age ${k.open_avg_age_d ?? "—"}d · tap to see them`} testid="lx-kpi-open" /></Tap>
      <Kpi label="Speed to lead" value={mins(k.speed_to_lead_min)} sub="median first reply" testid="lx-kpi-speed" />
      <Kpi label="Follow-up turnaround" value={k.turnaround_h === null ? "—" : `${k.turnaround_h < 0.1 ? "<0.1" : k.turnaround_h}h`} sub={`${k.touches_to_win ?? "—"} touches to win`} testid="lx-kpi-turnaround" />
    </div>
  );
};

export const LeadsExecView = ({ onOldView }) => {
  const [period, setPeriod] = useState("last30");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [pick, setPick] = useState(null);
  const [drill, setDrill] = useState({ data: null, loading: false });
  const drillRef = useRef(null);

  useEffect(() => {
    setData(null); setError(null); setPick(null);
    axios.get(`${API}/leads/exec`, { params: { period } }).then((r) => setData(r.data)).catch((e) => setError(e.response?.data?.detail || "Couldn't load the executive view"));
  }, [period]);

  useEffect(() => {
    if (!pick) return;
    setDrill({ data: null, loading: true });
    const params = pick.open_now ? { open_now: true } : { period, ...(pick.stage ? { stage: pick.stage } : {}), ...(pick.service ? { service: pick.service } : {}) };
    axios.get(`${API}/leads/exec/list`, { params }).then((r) => setDrill({ data: r.data, loading: false })).catch(() => setDrill({ data: { total: 0, leads: [] }, loading: false }));
    setTimeout(() => drillRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  }, [pick, period]);

  const label = PERIODS.find((p) => p[0] === period)[1];
  return (
    <div className="dc v3" style={{ background: "transparent" }} data-testid="leads-exec-view">
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 16, marginBottom: 20 }}>
        <div style={{ flex: "1 1 320px" }}>
          <h1 style={{ fontSize: 32, fontWeight: 800, letterSpacing: "-0.02em", margin: 0 }}>Leads Dashboard</h1>
          <p className="muted" style={{ margin: "6px 0 0", fontSize: 14 }}>Executive view: funnel, conversion and follow-up speed. Click any bar or tile to see the leads behind it.</p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <div className="tabs" data-testid="lx-period">
            {PERIODS.map(([k, l]) => <button key={k} className={`tab ${period === k ? "active" : ""}`} onClick={() => setPeriod(k)} data-testid={`lx-period-${k}`}>{l}</button>)}
          </div>
          <button className="btn btn-sm" onClick={onOldView} title={`The old view will be removed on ${OLD_VIEW_REMOVAL}`} data-testid="leads-old-view-btn"><History size={14} />Old view</button>
        </div>
      </div>
      {error && <div className="alert" data-testid="lx-error">{error}</div>}
      {!data && !error && <div className="skeleton" style={{ height: 420, borderRadius: 12 }} data-testid="lx-loading" />}
      {data && (
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          <Kpis k={data.kpis} onPick={setPick} />
          <ExecFunnel f={data.funnel} onPick={setPick} />
          <ExecTrend trend={data.trend} grain={data.grain} label={label} leadsIn={data.kpis.leads_in} prevLeadsIn={data.kpis.prev_leads_in} />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 24, alignItems: "start" }}>
            <ExecServices services={data.services} onPick={setPick} />
            <ExecOutcomes o={data.outcomes} />
          </div>
          <ExecReps reps={data.reps} showRevenue />
          {pick && <div ref={drillRef}><ExecDrill pick={pick} data={drill.data} loading={drill.loading} onClose={() => setPick(null)} /></div>}
        </div>
      )}
    </div>
  );
};
